<template>
  <div>
    <va-card class="overflow-auto">
      <vue-element-loading :active="visible" :is-full-screen="true" />

      <b-row class="pb-3">
        <b-col cols="10">
          <span style="font-size: 24px; font-weight: bold">ACCUSED/OFFENDERS</span>
        </b-col>
        <b-col cols="2" class="text-right">
          <b-button class="px-4" variant="secondary" @click="$router.push('/offenders/add')">
            Add Accused
          </b-button>
        </b-col>
      </b-row>

      <!-- Search Bar -->
      <b-row class="pb-4">
        <b-col cols="12">
          <b-input-group prepend="Search" class="mt-3">
            <b-form-input
              v-model="searchKeyword"
              placeholder="Search by name, alias, or ID number"
              @input="debouncedSearch"
            ></b-form-input>
          </b-input-group>
        </b-col>
      </b-row>

      <!-- Filters -->
      <div class="row mb-3">
        <div class="flex md3 sm6 xs12">
          <div class="form-group">
            <select class="filter pt-2 pb-2" style="width: 100% !important" v-model="threatLevelFilter"
              @change="filterOffenders">
              <option value="">All Threat Levels</option>
              <option value="Absconder">Absconder</option>
              <option value="Highly Active">Highly Active</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
              <option value="Dormant">Dormant</option>
              <option value="No More involved">No More involved</option>
            </select>
          </div>
        </div>

        <div class="flex md3 sm6 xs12">
          <div class="form-group">
            <select class="filter pt-2 pb-2" style="width: 100% !important" v-model="activityLevelFilter"
              @change="filterOffenders">
              <option value="">All Activity Levels</option>
              <option value="Highly dangerous">Highly dangerous</option>
              <option value="Dangerous">Dangerous</option>
              <option value="Harmless">Harmless</option>
              <option value="Unknown">Unknown</option>
            </select>
          </div>
        </div>

        <div class="flex md3 sm6 xs12">
          <div class="form-group">
            <select class="filter pt-2 pb-2" style="width: 100% !important" v-model="sexFilter"
              @change="filterOffenders">
              <option value="">All Genders</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
          </div>
        </div>

        <div class="flex md3 sm6 xs12">
          <div class="form-group">
            <select class="filter pt-2 pb-2" style="width: 100% !important" v-model="habitualFilter"
              @change="filterOffenders">
              <option value="">All Offenders</option>
              <option value="true">Habitual Only</option>
              <option value="false">Non-Habitual</option>
            </select>
          </div>
        </div>

        <div class="flex md3 sm6 xs12">
          <div class="form-group">
            <b-button variant="outline-secondary" @click="clearFilters">
              Clear Filters
            </b-button>
          </div>
        </div>
      </div>

      <!-- Offenders Table -->
      <div class="flex md12 sm6 xs12 overflow-auto">
        <div class="overflow-auto" style="overflow: auto">
          <b-table hover v-if="filteredOffenders.length" id="offenders-table" :fields="fields"
            :items="filteredOffenders" small borderless head-variant="dark" table-variant="light">
            <template v-slot:cell(name)="data">
              <strong>{{ data.item.name }}</strong>
              <br>
              <small class="text-muted">Alias: {{ data.item.alias || 'N/A' }}</small>
            </template>

            <template v-slot:cell(age)="data">
              {{ data.item.age }} years
              <br>
              <small class="text-muted">{{ data.item.sex }}</small>
            </template>

            <template v-slot:cell(offences)="data">
              <div style="max-width: 250px; white-space: normal; word-break: break-word;">
                <span class="badge badge-primary">{{ data.item.offences }}</span>
                <br>
                <span v-if="data.item.isHabitual" class="badge badge-danger mt-1">HABITUAL</span>
                <br>
                <small v-if="data.item.species" class="text-info mt-1 d-block" style="white-space: normal; word-break: break-word;">Species: {{ data.item.species }}</small>
              </div>
            </template>

            <template v-slot:cell(threatLevel)="data">
              <span :class="getThreatLevelClass(data.item.threatLevel)" class="badge">
                {{ data.item.threatLevel }}
              </span>
            </template>

            <template v-slot:cell(activityLevel)="data">
              <span :class="getActivityLevelClass(data.item.activityLevel)" class="badge">
                {{ data.item.activityLevel }}
              </span>
            </template>

            <template v-slot:cell(contactNo)="data">
              {{ data.item.contactNo || 'N/A' }}
              <br>
              <small class="text-muted">Aadhar: {{ data.item.adharNo || 'N/A' }}</small>
            </template>

            <template v-slot:cell(actions)="data">
              <div class="d-flex flex-wrap">
                <router-link :to="{
                  path: '/offender-details',
                  query: {
                    id: data.item._id
                  }
                }" class="mr-2 mb-2">
                  <b-button variant="info" size="sm">
                    View Details
                  </b-button>
                </router-link>
                <b-button class="mr-2 mb-2" variant="warning" size="sm" @click="editOffender(data.item)">
                  Edit
                </b-button>
                <b-button class="mb-2" variant="danger" size="sm" @click="deleteOffender(data.item._id)">
                  Delete
                </b-button>
              </div>
            </template>
          </b-table>

          <div v-else class="text-center py-4">
            <p>No offenders found</p>
          </div>

          <!-- Pagination -->
          <b-pagination v-if="totalRows > 0" align="center" v-model="currentPage" :total-rows="totalRows"
            :per-page="perPage" prev-text="Prev" next-text="Next" @change="nextPage"
            aria-controls="offenders-table"></b-pagination>
        </div>
      </div>
    </va-card>
  </div>
</template>

<script>
import axios from 'axios'
import VueElementLoading from 'vue-element-loading'

export default {
  name: 'Offenders',
  components: {
    VueElementLoading,
  },
  data () {
    return {
      visible: false,
      searchKeyword: '',
      threatLevelFilter: '',
      activityLevelFilter: '',
      sexFilter: '',
      habitualFilter: '',
      offenders: [],
      filteredOffenders: [],
      currentPage: 1,
      perPage: 10,
      totalRows: 0,
      searchTimeout: null,
      fields: [
        { key: 'name', label: 'Name/Alias' },
        { key: 'age', label: 'Age/Gender' },
        { key: 'offences', label: 'Offences/Species' },
        { key: 'threatLevel', label: 'Threat Level' },
        { key: 'activityLevel', label: 'Activity Level' },
        { key: 'contactNo', label: 'Contact/Aadhar' },
        { key: 'actions', label: 'Actions' },
      ],
    }
  },
  async mounted () {
    await this.fetchOffenders()
  },
  beforeDestroy () {
    // Clear search timeout to prevent memory leaks
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
  },
  methods: {
    async fetchOffenders () {
      this.visible = true
      try {
        // Build query parameters
        const params = {}

        if (this.searchKeyword && this.searchKeyword.trim() !== '') {
          params.search = this.searchKeyword.trim()
        }

        if (this.threatLevelFilter && this.threatLevelFilter !== '') {
          params.threatLevel = this.threatLevelFilter
        }

        if (this.activityLevelFilter && this.activityLevelFilter !== '') {
          params.activityLevel = this.activityLevelFilter
        }

        if (this.sexFilter && this.sexFilter !== '') {
          params.gender = this.sexFilter
        }

        if (this.habitualFilter && this.habitualFilter !== '') {
          params.offenderType = this.habitualFilter === 'true' ? 'habitual' : 'normal'
        }

        params.page = this.currentPage
        params.limit = this.perPage

        const response = await axios.get('/habitual/all', { params })
        if (response.data.status) {
          this.offenders = response.data.data
          this.filteredOffenders = [...this.offenders]
          this.totalRows = response.data.totalCount || this.filteredOffenders.length
        }
      } catch (error) {
        console.error('Error fetching offenders:', error)
        this.$toast.error('Failed to fetch offenders')
      } finally {
        this.visible = false
      }
    },

    async searchOffenders () {
      this.currentPage = 1
      await this.fetchOffenders()
    },

    debouncedSearch () {
      // Clear previous timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }

      // Set new timeout for 500ms delay
      this.searchTimeout = setTimeout(() => {
        this.searchOffenders()
      }, 500)
    },

    async filterOffenders () {
      this.currentPage = 1
      await this.fetchOffenders()
    },

    async clearFilters () {
      this.threatLevelFilter = ''
      this.activityLevelFilter = ''
      this.sexFilter = ''
      this.habitualFilter = ''
      this.searchKeyword = ''
      this.currentPage = 1
      await this.fetchOffenders()
    },

    nextPage (page) {
      this.currentPage = page
      this.fetchOffenders()
    },

    getThreatLevelClass (level) {
      switch (level) {
        case 'Absconder': return 'badge-danger'
        case 'Highly Active': return 'badge-warning'
        case 'Active': return 'badge-info'
        case 'Inactive': return 'badge-secondary'
        case 'Dormant': return 'badge-dark'
        case 'No More involved': return 'badge-light'
        default: return 'badge-secondary'
      }
    },

    getActivityLevelClass (level) {
      switch (level) {
        case 'Highly dangerous': return 'badge-danger'
        case 'Dangerous': return 'badge-warning'
        case 'Harmless': return 'badge-success'
        case 'Unknown': return 'badge-secondary'
        default: return 'badge-secondary'
      }
    },

    editOffender (offender) {
      // Navigate to edit page
      this.$router.push({
        path: '/edit-offender',
        query: { id: offender._id },
      })
    },

    async deleteOffender (id) {
      if (confirm('Are you sure you want to delete this offender?')) {
        try {
          const response = await axios.delete(`/habitual/${id}`)
          if (response.data.status) {
            // Add a small delay before reload to ensure the alert is shown
            setTimeout(() => {
              window.location.reload()
            }, 100)
          } else {
            alert('Failed to delete offender: ' + (response.data.message || 'Unknown error'))
          }
        } catch (error) {
          console.error('Error deleting offender:', error)
          const errorMessage = error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : error.message || 'Unknown error'
          alert('Failed to delete offender: ' + errorMessage)
        }
      }
    },

  },
}
</script>

<style scoped>
.filter {
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 8px;
}

.badge {
  font-size: 0.8em;
  padding: 4px 8px;
  white-space: normal;
  text-align: left;
}
</style>
